import NewsTable from "@/features/news/components/NewsTable";
import NewsUpsertDialog from "@/features/news/components/NewsUpsertDialog";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NewsArticle, SortField, useDeleteNews, useNews} from "@/hooks/useNews";
import HeaderTextButton from "@/components/HeaderTextButton.tsx";
import {useQueryModal} from "@/hooks/useQueryModal.ts";
import {useTableQuery} from "@/hooks/useTableQuery.ts";
import {useCategories} from "@/hooks/useCategories";
import {useTags} from "@/hooks/useTags.ts";

const MIN_SEARCH_LEN = 3;
const DEBOUNCE_MS = 300;

export default function AdminNewsPage() {
    const {
        searchParams,
        editId,
        isNew,
        modalOpen,
        setParam,
        openCreate,
        openEdit,
        closeModal,
    } = useQueryModal();

    const {
        page,
        size,
        sortBy,
        sortOrder,
        setPage,
        onRequestSort,
    } = useTableQuery<SortField>({
        searchParams,
        setParam,
        defaultPage: 0,
        defaultSize: 10,
        defaultSortBy: "publishedAt",
        defaultSortOrder: "desc",
    });

    const qFromUrl = useMemo(() => searchParams.get("q") ?? "", [searchParams]);
    const [query, setQuery] = useState(qFromUrl);
    const lastAppliedQuery = useRef(qFromUrl);

    useEffect(() => setQuery(qFromUrl), [qFromUrl]);

    useEffect(() => {
        const id = setTimeout(() => {
            const v = query.trim();

            // Only update URL (and reset page) if the value differs from what’s already applied
            if (v.length >= MIN_SEARCH_LEN) {
                if (v !== lastAppliedQuery.current) {
                    setParam((p) => {
                        p.set("q", v);
                        p.set("page", "0");
                    });
                    lastAppliedQuery.current = v;
                }
            } else {
                // v too short → remove q only if it exists
                if (lastAppliedQuery.current) {
                    setParam((p) => {
                        p.delete("q");
                        p.set("page", "0");
                    });
                    lastAppliedQuery.current = "";
                }
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(id);
    }, [query, setParam]);
    const goToPage = useCallback(
        (next: number) => {
            setParam((p) => p.set("page", String(Math.max(0, next))));
        },
        [setParam]
    );

    const {
        data: result = {page, size, total: 0, items: []} as any,
        isLoading,
        isError
    } = useNews(true, {
        page, size, sortBy, sortOrder,
        q: qFromUrl || undefined,
    });


    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const v = query.trim();
        if (v.length >= MIN_SEARCH_LEN) {
            if (v !== lastAppliedQuery.current) {
                setParam((p) => {
                    p.set("q", v);
                    p.set("page", "0");
                });
                lastAppliedQuery.current = v;
            }
        } else if (lastAppliedQuery.current) {
            setParam((p) => {
                p.delete("q");
                p.set("page", "0");
            });
            lastAppliedQuery.current = "";
        }
    };

    const {mutateAsync: deleteNews} = useDeleteNews();

    const {
        data: categoriesResult = {page: 0, size: 0, total: 0, items: []} as any,
    } = useCategories({page: 0, size: 100, sortBy: "name", sortOrder: "asc"});

    const {
        data: tagsResult = {page: 0, size: 0, total: 0, items: []} as any,
    } = useTags({page: 0, size: 100, sortBy: "name", sortOrder: "asc"});

    const initialForDialog: Partial<NewsArticle> | null = useMemo(() => {
        if (isNew) return null;
        if (editId) return result.items.find((c: NewsArticle) => c.id === editId) ?? null;
        return null;
    }, [isNew, editId, result]);

    return (
        <>
            <HeaderTextButton
                title="Articles"
                description="Create and manage public news articles."
                label="Add Article"
                onClick={openCreate}
            />

            <div className="mt-2">
                <input
                    id="search"
                    name="search"
                    type="text"
                    placeholder="Search articles..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
            </div>

            <NewsTable
                content={result}
                isLoading={isLoading}
                isError={isError}
                onCreate={openCreate}
                onEdit={openEdit}
                onDelete={deleteNews}
                setPage={setPage}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onRequestSort={onRequestSort}
            />

            <NewsUpsertDialog
                open={modalOpen}
                initial={initialForDialog}
                categories={categoriesResult}
                tags={tagsResult}
                onClose={closeModal}
            />
        </>
    );
}
